import { useRef } from 'react';
import type { ReactNode } from 'react';

import Button from 'components/partials/button/button';

import { useFocusOnOpen } from 'hooks/use-focus-on-open';

interface QuickTipProps {
  title: string;
  body: ReactNode;
  currentStep: number;
  totalSteps: number;
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
}

const QuickTip = ({
  title,
  body,
  currentStep,
  totalSteps,
  isLast,
  onNext,
  onSkip,
}: QuickTipProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useFocusOnOpen(containerRef, true);

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      className="outline-none flex flex-col gap-4"
      data-testid="feature-hint-quick-tip"
    >
      <div className="flex flex-col gap-1">
        <div className="text-base font-bold leading-6 text-navy-800">
          {title}
        </div>

        <div className="text-sm text-gray-700">{body}</div>
      </div>

      <div className="flex items-center">
        {!isLast && (
          <Button
            type="button"
            variant="reserve-text-link"
            size="sm"
            className="p-0"
            onClick={onSkip}
            data-testid="feature-hint-skip"
          >
            Skip tour
          </Button>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <span
            className="text-sm text-gray-500"
            data-testid="feature-hint-counter"
          >
            {currentStep} of {totalSteps}
          </span>

          <Button
            type="button"
            variant="reserve-filled"
            size="xs"
            onClick={onNext}
            data-testid="feature-hint-next"
          >
            {isLast ? 'Done' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickTip;
